import type { NoteName, Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { isChordVariant } from '../chord/chord-types.js'
import { getChordNotes } from '../chord/get-notes.js'
import { getName as getChordName } from '../chord/get-name.js'
import { NoteLib } from '../note/index.js'
import ScaleLib from '../scale/scale.js'
import type { MelodyNoteClassification } from './types.js'

export interface ClassifyMelodyNoteInput {
  note: { pitch: NoteName; octave: number; duration: string; position: number }
  chord: Chord
  scale: Scale
  prev?: { pitch: NoteName; octave: number; duration: string; position: number }
  next?: { pitch: NoteName; octave: number; duration: string; position: number }
}

/**
 * Classify a melody note relative to its chord and scale context.
 *
 * Layer-1 implementation: chord-tone vs anything-else only. Subsequent
 * milestones widen the union of classifications (Milestone 5: passing /
 * neighbor / chromatic-passing; Milestone 6: tension with availability
 * gating). The function signature is the v1 stable contract.
 *
 * Priority order (first match wins):
 *   1. chord-tone (note is in `getChordNotes(chord)`)
 *   2. modal-characteristic (Milestone 1 — extends to mode awareness)
 *   3. out-of-key (Milestone 1 — note is not in `ScaleLib.getNotes(scale)`)
 *   4. in-scale (Milestone 1 — default fallthrough for in-scale non-chord-tones)
 *
 * The chord-tone branch maps the chord's note position to the lexicon id
 * for the root / 3rd / 5th / 7th. The mapping uses chromatic-index distance
 * from the chord root.
 */
export const classifyMelodyNote = (input: ClassifyMelodyNoteInput): MelodyNoteClassification => {
  const { note, chord } = input

  const chordName = getChordName(chord)
  const chordNotes = getChordNotes(chordName)
  const noteIdx = NoteLib.getNoteChromaticIndex(note.pitch)
  const chordIdx = chordNotes.map(n => NoteLib.getNoteChromaticIndex(n))

  if (chordIdx.includes(noteIdx)) {
    const rootNote = chordRoot(chord, chordNotes)
    const rootIdx = NoteLib.getNoteChromaticIndex(rootNote)
    const interval = (noteIdx - rootIdx + 12) % 12
    const lexiconId = chordToneInterval(interval)
    return { kind: 'chord-tone', lexiconId }
  }

  // Modal-characteristic check — reuses the same interval-aware lookup as
  // detectModalSignature. A note is modal-characteristic when it equals the
  // mode's signature pitch class AND the scale spec carries the expected
  // alteration at that degree. Bowie's §8 Q3 verdict: fires at any placement
  // (scale property, not chord property).
  const modalLexiconId = matchModalSignature(input.scale, noteIdx)
  if (modalLexiconId) {
    return { kind: 'modal-characteristic', lexiconId: modalLexiconId }
  }

  // Note is not a chord tone. Check scale membership.
  const scaleNotes = ScaleLib.getNotes(input.scale)
  const scaleIdx = scaleNotes.map(n => NoteLib.getNoteChromaticIndex(n))
  if (scaleIdx.includes(noteIdx)) {
    return { kind: 'in-scale', lexiconId: 'melody-in-scale' }
  }
  if (isLeadingToneInflection(input.scale, noteIdx)) {
    return { kind: 'chromatic-passing', lexiconId: 'melody-leading-tone-inflection' }
  }
  return { kind: 'out-of-key', lexiconId: 'melody-out-of-key' }
}

/**
 * Per-mode signature lookup, mirroring `MODE_SIGNATURES` in
 * `analysis/modal-signature.ts`. Kept local to avoid coupling the melody
 * module to the chord-window analyzer; values are identical and would
 * drift only if Bowie changed the underlying convention. A follow-up
 * refactor may extract these to `lib/music-lib/src/modal/signatures.ts`.
 */
const MODE_SIGNATURES_LOCAL: Record<
  'dorian' | 'mixolydian' | 'phrygian' | 'lydian',
  { degreeIndex: number; expectedAlteration: string; lexiconId: string }
> = {
  dorian: { degreeIndex: 5, expectedAlteration: 'VI', lexiconId: 'mode-dorian-signature' },
  mixolydian: { degreeIndex: 6, expectedAlteration: 'bVII', lexiconId: 'mode-mixolydian-signature' },
  phrygian: { degreeIndex: 1, expectedAlteration: 'bII', lexiconId: 'mode-phrygian-signature' },
  lydian: { degreeIndex: 3, expectedAlteration: '#IV', lexiconId: 'mode-lydian-signature' },
}

const isSupportedMode = (
  type: string | undefined
): type is keyof typeof MODE_SIGNATURES_LOCAL =>
  type === 'dorian' || type === 'mixolydian' || type === 'phrygian' || type === 'lydian'

const matchModalSignature = (scale: Scale, noteChromaticIdx: number): string | null => {
  if (!isSupportedMode(scale.type)) {
    return null
  }
  const config = MODE_SIGNATURES_LOCAL[scale.type]
  const { intervals } = ScaleLib.getScaleSpec(scale)
  if (intervals[config.degreeIndex] !== config.expectedAlteration) {
    return null
  }
  const scaleNotes = ScaleLib.getNotes(scale)
  const signatureNote = scaleNotes[config.degreeIndex]
  if (!signatureNote) {
    return null
  }
  return NoteLib.getNoteChromaticIndex(signatureNote) === noteChromaticIdx ? config.lexiconId : null
}

/**
 * Detect the conventional melodic-minor leading-tone inflection — a raised 7
 * in a natural-minor scale. The note one semitone below the tonic is a
 * teachable inflection, not a chromatic error. Mirrors Quincy's V-in-minor
 * harmony rule (PR #1343) at the melody level. Other modes (Dorian,
 * Phrygian, etc.) inherit their own raised-7 inflections; v1 only ships
 * the natural-minor case (the most common).
 */
const isLeadingToneInflection = (scale: Scale, noteChromaticIdx: number): boolean => {
  if (scale.type !== 'minor') {
    return false
  }
  // In natural minor the leading tone (raised 7) sits one semitone above the
  // scale's diatonic flat-7 — equivalently, one semitone below the tonic.
  const tonicIdx = NoteLib.getNoteChromaticIndex(scale.root)
  const leadingToneIdx = (tonicIdx + 11) % 12
  return noteChromaticIdx === leadingToneIdx
}

/**
 * Resolve the chord's root pitch class. `ChordVariant` carries `root`
 * explicitly; `ChordNotes` (the note-array form) falls back to the first
 * note in the array — by convention, the lowest-listed note is the root
 * unless an inversion is intended (v1 does not model inversions).
 */
const chordRoot = (chord: Chord, chordNotes: NoteName[]): NoteName => {
  if (isChordVariant(chord)) {
    return chord.root
  }
  return chordNotes[0]
}

const chordToneInterval = (interval: number): string => {
  // Major-3rd = 4 semitones, minor-3rd = 3 semitones, perfect-5th = 7.
  // 7th: maj7 = 11, dom7 = 10. Diminished 5th = 6 (handled as chord-tone-5
  // since it's still the chord's 5th degree). v1 maps by interval-size class.
  if (interval === 0) {
    return 'melody-chord-tone-1'
  }
  if (interval === 3 || interval === 4) {
    return 'melody-chord-tone-3'
  }
  if (interval === 6 || interval === 7 || interval === 8) {
    return 'melody-chord-tone-5'
  }
  if (interval === 10 || interval === 11) {
    return 'melody-chord-tone-7'
  }
  // Extended tones (9, 11, 13) over a chord that contains them. v1 lumps
  // these under chord-tone-5; Milestone 6 reclassifies as tension.
  return 'melody-chord-tone-5'
}
